import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  alpha: number;
  color: string;
  pulsePhase: number;
  pulseSpeed: number;
}

const COLORS = [
  "14,165,160",   // brand teal
  "170,255,69",   // brand lime
  "6,182,212",    // cyan
  "16,185,129",   // emerald
  "255,255,255",  // white
  "94,234,212",   // teal-300
];

export default function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let particles: Particle[] = [];

    const resize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
      initParticles();
    };

    const initParticles = () => {
      const count = Math.min(Math.floor((width * height) / 7000), 160);
      particles = Array.from({ length: count }, () => createParticle());
    };

    const createParticle = (): Particle => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 2.5 + 0.8,
      alpha: Math.random() * 0.6 + 0.3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      pulsePhase: Math.random() * Math.PI * 2,
      pulseSpeed: 0.015 + Math.random() * 0.025,
    });

    // Glow orb with strong halo
    const drawGlowOrb = (x: number, y: number, r: number, color: string, alpha: number) => {
      // Outer halo — large and soft
      const outerGrd = ctx.createRadialGradient(x, y, 0, x, y, r * 10);
      outerGrd.addColorStop(0, `rgba(${color},${Math.min(alpha * 0.9, 0.9)})`);
      outerGrd.addColorStop(0.2, `rgba(${color},${alpha * 0.5})`);
      outerGrd.addColorStop(0.5, `rgba(${color},${alpha * 0.15})`);
      outerGrd.addColorStop(1, `rgba(${color},0)`);
      ctx.beginPath();
      ctx.arc(x, y, r * 10, 0, Math.PI * 2);
      ctx.fillStyle = outerGrd;
      ctx.fill();

      // Inner bright core
      const coreGrd = ctx.createRadialGradient(x, y, 0, x, y, r * 1.5);
      coreGrd.addColorStop(0, `rgba(255,255,255,${Math.min(alpha * 1.2, 1)})`);
      coreGrd.addColorStop(0.5, `rgba(${color},${Math.min(alpha, 1)})`);
      coreGrd.addColorStop(1, `rgba(${color},0)`);
      ctx.beginPath();
      ctx.arc(x, y, r * 1.5, 0, Math.PI * 2);
      ctx.fillStyle = coreGrd;
      ctx.fill();
    };

    // Connections with visible glow
    const drawConnections = (p: Particle, others: Particle[]) => {
      const CONNECT_DIST = 150;
      const MOUSE_DIST = 220;

      for (const other of others) {
        if (other === p) continue;
        const dx = p.x - other.x;
        const dy = p.y - other.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < CONNECT_DIST) {
          const lineAlpha = (1 - dist / CONNECT_DIST) * 0.55;
          const grd = ctx.createLinearGradient(p.x, p.y, other.x, other.y);
          grd.addColorStop(0, `rgba(${p.color},${lineAlpha})`);
          grd.addColorStop(1, `rgba(${other.color},${lineAlpha})`);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(other.x, other.y);
          ctx.strokeStyle = grd;
          ctx.lineWidth = 0.9;
          ctx.stroke();
        }
      }

      // Mouse lines — bright lime green
      const mdx = p.x - mouseRef.current.x;
      const mdy = p.y - mouseRef.current.y;
      const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
      if (mdist < MOUSE_DIST) {
        const lineAlpha = (1 - mdist / MOUSE_DIST) * 0.75;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(mouseRef.current.x, mouseRef.current.y);
        ctx.strokeStyle = `rgba(170,255,69,${lineAlpha})`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }
    };

    // Slowly rotating light beams
    const drawLightRays = (time: number) => {
      const beams = [
        { x: width * 0.05, y: height * 0.1, color: "14,165,160", alpha: 0.18, speed: 0.00015 },
        { x: width * 0.6, y: 0, color: "6,182,212", alpha: 0.14, speed: 0.0001 },
        { x: width * 0.3, y: height * 0.9, color: "170,255,69", alpha: 0.10, speed: 0.00012 },
      ];

      beams.forEach((b, i) => {
        const angle = time * b.speed + i * 1.3;
        const len = Math.max(width, height) * 1.8;
        const spread = 0.12;

        const grd = ctx.createLinearGradient(
          b.x, b.y,
          b.x + Math.cos(angle) * len,
          b.y + Math.sin(angle) * len
        );
        grd.addColorStop(0, `rgba(${b.color},${b.alpha})`);
        grd.addColorStop(0.3, `rgba(${b.color},${b.alpha * 0.5})`);
        grd.addColorStop(1, `rgba(${b.color},0)`);

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(b.x, b.y);
        ctx.lineTo(b.x + Math.cos(angle - spread) * len, b.y + Math.sin(angle - spread) * len);
        ctx.lineTo(b.x + Math.cos(angle + spread) * len, b.y + Math.sin(angle + spread) * len);
        ctx.closePath();
        ctx.fillStyle = grd;
        ctx.fill();
        ctx.restore();
      });
    };

    // Large ambient orbs — much brighter
    const drawAmbientOrbs = (time: number) => {
      const orbs = [
        { bx: 0.08, by: 0.35, r: 320, color: "14,165,160", baseAlpha: 0.22, speed: 0.0006, phase: 0 },
        { bx: 0.88, by: 0.55, r: 260, color: "6,182,212", baseAlpha: 0.18, speed: 0.0005, phase: 2.1 },
        { bx: 0.45, by: 0.85, r: 200, color: "170,255,69", baseAlpha: 0.14, speed: 0.0007, phase: 4.2 },
        { bx: 0.75, by: 0.15, r: 180, color: "94,234,212", baseAlpha: 0.12, speed: 0.0004, phase: 1.0 },
      ];

      orbs.forEach((orb) => {
        const pulse = Math.sin(time * 0.001 + orb.phase) * 0.05;
        const alpha = orb.baseAlpha + pulse;
        const ox = orb.bx * width + Math.sin(time * orb.speed + orb.phase) * 40;
        const oy = orb.by * height + Math.cos(time * orb.speed * 0.8 + orb.phase) * 30;

        const grd = ctx.createRadialGradient(ox, oy, 0, ox, oy, orb.r);
        grd.addColorStop(0, `rgba(${orb.color},${alpha})`);
        grd.addColorStop(0.4, `rgba(${orb.color},${alpha * 0.5})`);
        grd.addColorStop(1, `rgba(${orb.color},0)`);

        ctx.beginPath();
        ctx.arc(ox, oy, orb.r, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();
      });
    };

    // Occasional sparkle flares
    const flares: { x: number; y: number; life: number; maxLife: number; color: string }[] = [];
    let flareTimer = 0;

    const spawnFlare = () => {
      flares.push({
        x: Math.random() * width,
        y: Math.random() * height,
        life: 0,
        maxLife: 60 + Math.random() * 40,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      });
    };

    const drawFlares = () => {
      for (let i = flares.length - 1; i >= 0; i--) {
        const f = flares[i];
        f.life++;
        const t = f.life / f.maxLife;
        const alpha = t < 0.5 ? t * 2 : (1 - t) * 2;
        const r = (t < 0.5 ? t * 2 : 1) * 40;

        const grd = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, r);
        grd.addColorStop(0, `rgba(255,255,255,${alpha * 0.9})`);
        grd.addColorStop(0.2, `rgba(${f.color},${alpha * 0.7})`);
        grd.addColorStop(1, `rgba(${f.color},0)`);
        ctx.beginPath();
        ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        if (f.life >= f.maxLife) flares.splice(i, 1);
      }
    };

    let lastTime = 0;
    const animate = (time: number) => {
      animRef.current = requestAnimationFrame(animate);
      const dt = Math.min(time - lastTime, 32);
      lastTime = time;
      void dt;

      ctx.clearRect(0, 0, width, height);

      // Ambient background orbs
      drawAmbientOrbs(time);

      // Light rays
      drawLightRays(time);

      // Connections
      for (const p of particles) drawConnections(p, particles);

      // Particles
      for (const p of particles) {
        // Mouse repel
        const mdx = p.x - mouseRef.current.x;
        const mdy = p.y - mouseRef.current.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < 140 && mdist > 0) {
          const force = (140 - mdist) / 140 * 0.018;
          p.vx += (mdx / mdist) * force;
          p.vy += (mdy / mdist) * force;
        }

        p.vx *= 0.985;
        p.vy *= 0.985;
        p.vx = Math.max(-1.5, Math.min(1.5, p.vx));
        p.vy = Math.max(-1.5, Math.min(1.5, p.vy));

        p.x += p.vx;
        p.y += p.vy;

        p.pulsePhase += p.pulseSpeed;
        const pulseAlpha = p.alpha + Math.sin(p.pulsePhase) * 0.3;

        if (p.x < -20) p.x = width + 20;
        if (p.x > width + 20) p.x = -20;
        if (p.y < -20) p.y = height + 20;
        if (p.y > height + 20) p.y = -20;

        drawGlowOrb(p.x, p.y, p.radius, p.color, Math.max(0.15, Math.min(1, pulseAlpha)));
      }

      // Flares
      flareTimer++;
      if (flareTimer > 50) { spawnFlare(); flareTimer = 0; }
      drawFlares();
    };

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onMouseLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };

    window.addEventListener("resize", resize);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseleave", onMouseLeave);
    resize();
    animRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ mixBlendMode: "screen", zIndex: 1 }}
    />
  );
}
